import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { to12h } from "./shared";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { ServiceSelect } from "./ServiceSelect";
import { Plus, Trash2 } from "lucide-react";
import { istNow, ymd } from "../lib/dates";

const today = () => ymd(istNow());
const addMin = (hhmm, m) => { const [h, mm] = (hhmm || "0:0").split(":").map(Number); const t = h * 60 + mm + (Number(m) || 0); return `${String(Math.floor(t / 60) % 24).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`; };

// Shared "New Appointment" booking dialog. Used inline by both the Appointments page
// and the Billing/POS timeline so front-desk staff never lose their page context.
export default function NewAppointmentDialog({ open, onOpenChange, branches = [], services = [], staff = [], initial, onSaved }) {
  const [form, setForm] = useState({ branch_id: "", date: today(), start_time: "", customer_name: "", customer_mobile: "", customer_gender: "", customer_id: null });
  const [svcRows, setSvcRows] = useState([{ service_id: "", staff_id: "", duration: 30 }]);
  const [fpCfg, setFpCfg] = useState({ enabled: false, slabs: [], services: [] });
  const [fpSlabA, setFpSlabA] = useState(null);
  const [fpQ, setFpQ] = useState("");
  const [slots, setSlots] = useState([]);
  const [offMsg, setOffMsg] = useState("");
  const [mobileSug, setMobileSug] = useState([]);
  const [showMobileSug, setShowMobileSug] = useState(false);

  // Branch-aware Freedom Package config (same source/shape the POS builder uses).
  useEffect(() => {
    const bid = form.branch_id;
    api.get("/freedom-package", { params: bid ? { branch_id: bid } : {} }).then((r) => setFpCfg(r.data)).catch(() => {});
  }, [form.branch_id]);

  // Reset the form each time the dialog opens, seeding from `initial` (slot/service prefill).
  useEffect(() => {
    if (!open) return;
    const init = initial || {};
    setForm({
      branch_id: init.branch_id || branches[0]?.id || "",
      date: init.date || today(),
      start_time: init.start_time || "",
      customer_name: init.customer_name || "",
      customer_mobile: init.customer_mobile || "",
      customer_gender: "",
      customer_id: null,
    });
    setSvcRows([{ service_id: init.service_id || "", staff_id: init.staff_id || "", duration: init.duration || 30 }]);
    setFpSlabA(null);
    setMobileSug([]); setShowMobileSug(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Availability slots follow the first service row's staff + duration.
  useEffect(() => {
    const first = svcRows[0];
    setOffMsg("");
    if (open && form.branch_id && first?.staff_id && form.date) {
      api.get("/appointments/public/staff-day-status", { params: { staff_id: first.staff_id, date: form.date } }).then((st) => {
        if (st.data?.off) { setOffMsg(st.data.message); setSlots([]); return; }
        api.get("/appointments/availability/slots", { params: { branch_id: form.branch_id, staff_id: first.staff_id, date: form.date, duration: first.duration || 30 } })
          .then((r) => setSlots(r.data)).catch(() => setSlots([]));
      }).catch(() => setSlots([]));
    } else { setSlots([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.branch_id, form.date, svcRows]);

  // Mobile-number customer lookup (auto-fills an existing customer on 10-digit exact match).
  useEffect(() => {
    const digits = (form.customer_mobile || "").replace(/\D/g, "");
    if (form.customer_id) return;
    if (digits.length < 4) { setMobileSug([]); setShowMobileSug(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers/search", { params: { q: digits } });
        setMobileSug(data); setShowMobileSug(true);
        if (digits.length === 10) {
          const exact = data.find((c) => (c.mobile || "").replace(/\D/g, "") === digits);
          if (exact) selectCust(exact);
        }
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.customer_mobile, form.customer_id]);

  const selectCust = (c) => {
    setForm((f) => ({ ...f, customer_id: c.id, customer_name: c.name || f.customer_name, customer_mobile: c.mobile || f.customer_mobile, customer_gender: c.gender || f.customer_gender }));
    setMobileSug([]); setShowMobileSug(false);
  };

  // Add a brand-new client from the appointment popup (same DB as Billing/POS). Never duplicates
  // an existing mobile — selects the existing customer instead.
  const addClient = async () => {
    const mobile = (form.customer_mobile || "").replace(/\D/g, "");
    if (!form.customer_name?.trim()) { toast.error("Enter the customer name first"); return; }
    if (mobile.length < 10) { toast.error("Enter a valid 10-digit mobile number"); return; }
    const exact = mobileSug.find((c) => (c.mobile || "").replace(/\D/g, "") === mobile);
    if (exact) { selectCust(exact); toast.message("Existing customer selected"); return; }
    try {
      const { data } = await api.post("/customers", { name: form.customer_name.trim(), mobile: form.customer_mobile, gender: form.customer_gender || "", preferred_branch: form.branch_id || null });
      selectCust(data); toast.success("Client added");
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (/exist|duplicate|already/i.test(String(detail))) {
        try {
          const { data } = await api.get("/customers/search", { params: { q: mobile } });
          const ex = data.find((c) => (c.mobile || "").replace(/\D/g, "") === mobile);
          if (ex) { selectCust(ex); toast.message("Existing customer selected"); return; }
        } catch { /* ignore */ }
      }
      toast.error(apiError(detail));
    }
  };

  const setSvcRow = (i, patch) => setSvcRows((rs) => { const c = [...rs]; c[i] = { ...c[i], ...patch }; return c; });
  const addSvcRow = () => setSvcRows((rs) => [...rs, { service_id: "", staff_id: "", duration: 30 }]);
  const delSvcRow = (i) => setSvcRows((rs) => rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs);

  const branchStaff = staff.filter((s) => !form.branch_id || s.branch_id === form.branch_id);

  // ---- Freedom Package selection (mirrors the working POS builder) ----
  const slabObj = fpSlabA ? fpCfg.slabs.find((s) => s.count === fpSlabA) : null;
  const fpSelectedIds = svcRows.filter((r) => r.service_id).map((r) => r.service_id);
  const fpStaffFor = (serviceId) => {
    const active = branchStaff.filter((st) => String(st.status || "").toLowerCase() !== "inactive");
    const elig = active.filter((st) => Array.isArray(st.services) && st.services.length && st.services.includes(serviceId));
    return elig.length ? elig : active;
  };
  const toggleFpSvc = (svc) => setSvcRows((rs) => {
    const kept = rs.filter((r) => r.service_id);
    if (kept.some((r) => r.service_id === svc.id)) return kept.filter((r) => r.service_id !== svc.id);
    if (slabObj && kept.length >= slabObj.count) return kept;
    return [...kept, { service_id: svc.id, staff_id: "", duration: svc.duration || 30 }];
  });
  const setFpStaff = (serviceId, staffId) => setSvcRows((rs) => rs.map((r) => r.service_id === serviceId ? { ...r, staff_id: staffId } : r));

  const schedule = (() => { let cur = form.start_time; return svcRows.map((r) => { if (!cur) return { ...r, start: "", end: "" }; const end = addMin(cur, r.duration || 30); const seg = { ...r, start: cur, end }; cur = end; return seg; }); })();
  const totalMin = svcRows.reduce((s, r) => s + (Number(r.duration) || 0), 0);

  const save = async () => {
    const slab = fpSlabA ? fpCfg.slabs.find((s) => s.count === fpSlabA) : null;
    const group = slab ? `FP-${Date.now()}` : null;
    const svcList = svcRows.filter((r) => r.service_id && r.staff_id).map((r) => ({
      service_id: r.service_id, service_name: services.find((s) => s.id === r.service_id)?.name || "",
      staff_id: r.staff_id, staff_name: staff.find((s) => s.id === r.staff_id)?.name || "",
      duration: Number(r.duration) || 30,
      ...(slab ? { freedom_group: group, freedom_slab_count: slab.count, freedom_slab_price: slab.price } : {}),
    }));
    if (!svcList.length) { toast.error("Add at least one service"); return; }
    if (slab && svcList.length !== slab.count) { toast.error(`Freedom Package (Any ${slab.count}) needs exactly ${slab.count} services, each with staff.`); return; }
    try {
      await api.post("/appointments/multi", {
        customer_id: form.customer_id, customer_name: form.customer_name, customer_mobile: form.customer_mobile,
        customer_gender: form.customer_gender, branch_id: form.branch_id, date: form.date,
        start_time: form.start_time, services: svcList,
      });
      toast.success("Appointment booked"); onOpenChange(false); onSaved && onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (/weekly off|already passed/i.test(String(detail))) {
        toast.error(String(detail));
      } else if (/not available|overlap|choose another time/i.test(String(detail))) {
        toast.error("This staff member is not available for the complete service duration.");
        const first = svcRows.find((r) => r.staff_id);
        if (first) {
          try {
            const { data } = await api.get("/appointments/availability/slots", { params: { branch_id: form.branch_id, staff_id: first.staff_id, date: form.date, duration: first.duration || 30 } });
            const alts = data.filter((x) => x.available).slice(0, 4).map((x) => to12h(x.time));
            if (alts.length) toast.message("Available alternatives", { description: alts.join(" · ") });
          } catch { /* ignore */ }
        }
      } else { toast.error(apiError(detail)); }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden box-border [&>*]:min-w-0">
        <DialogHeader><DialogTitle className="font-display text-2xl">New Appointment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Customer Name</Label><Input data-testid="appt-customer-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="bg-secondary mt-1" /></div>
            <div className="relative"><Label className="text-xs">Mobile</Label>
              <Input data-testid="appt-customer-mobile" value={form.customer_mobile} autoComplete="off"
                onChange={(e) => setForm({ ...form, customer_mobile: e.target.value, customer_id: null })}
                onFocus={() => { if (mobileSug.length) setShowMobileSug(true); }}
                onBlur={() => setTimeout(() => setShowMobileSug(false), 200)}
                placeholder="Type 4+ digits to search" className="bg-secondary mt-1" />
              {showMobileSug && !form.customer_id && (
                <div className="absolute z-30 left-0 right-0 mt-1 bg-[#0f1c16] border border-border rounded-sm shadow-2xl overflow-hidden" data-testid="appt-mobile-suggestions">
                  {mobileSug.map((s) => (
                    <button key={s.id} type="button" onMouseDown={(e) => { e.preventDefault(); selectCust(s); }} data-testid={`appt-mobile-suggest-${s.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-secondary border-b border-border/50 flex items-center justify-between gap-2">
                      <span className="font-medium text-sm truncate">{s.name}</span>
                      <span className="text-primary text-sm font-mono shrink-0">{s.mobile}</span>
                    </button>
                  ))}
                  <div className="flex items-center justify-between gap-2 p-2 bg-secondary/40" data-testid="appt-mobile-none">
                    <span className="text-xs text-muted-foreground truncate">{mobileSug.length ? "Not listed? Add as new" : "No match found"}</span>
                    <Button size="sm" type="button" className="rounded-sm h-7 shrink-0 bg-primary text-primary-foreground hover:bg-[#D4AF37]"
                      onMouseDown={(e) => { e.preventDefault(); addClient(); }} data-testid="appt-add-client">
                      <Plus size={13} className="mr-1" /> Add Client
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div><Label className="text-xs">Gender</Label>
            <Select value={form.customer_gender} onValueChange={(v) => setForm({ ...form, customer_gender: v })}>
              <SelectTrigger className="bg-secondary mt-1" data-testid="appt-customer-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
              <SelectContent>{["Male", "Female", "Other"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Branch</Label>
            <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
              <SelectTrigger className="bg-secondary mt-1" data-testid="appt-branch"><SelectValue placeholder="Branch" /></SelectTrigger>
              <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {fpCfg.enabled && fpCfg.slabs.length > 0 && (
            <div data-testid="appt-freedom-block">
              <Label className="text-xs">Freedom Package (optional)</Label>
              <Select value={fpSlabA ? String(fpSlabA) : "none"} onValueChange={(v) => { const n = v === "none" ? null : Number(v); setFpSlabA(n); setFpQ(""); setSvcRows(n ? [] : [{ service_id: "", staff_id: "", duration: 30 }]); }}>
                <SelectTrigger className="bg-secondary mt-1" data-testid="appt-freedom-slab"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not a Freedom Package</SelectItem>
                  {fpCfg.slabs.map((s) => <SelectItem key={s.count} value={String(s.count)}>Any {s.count} Services — ₹{s.price}</SelectItem>)}
                </SelectContent>
              </Select>
              {fpSlabA && <p className="text-[11px] text-primary mt-1">Select exactly {fpSlabA} eligible services below, each with its own staff. Final package price ₹{fpCfg.slabs.find((s) => s.count === fpSlabA)?.price}.</p>}
            </div>
          )}
          {fpSlabA ? (
            <div className="space-y-2" data-testid="appt-fp-list">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Eligible Services</Label>
                <span className="text-[11px] text-muted-foreground" data-testid="appt-fp-count">{fpSelectedIds.length}/{fpSlabA} selected</span>
              </div>
              <Input value={fpQ} onChange={(e) => setFpQ(e.target.value)} placeholder="Search services…" className="bg-secondary h-9" data-testid="appt-fp-search" />
              <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
                {(fpCfg.services || []).filter((s) => { const t = fpQ.trim().toLowerCase(); return !t || (s.name || "").toLowerCase().includes(t) || (s.category || "").toLowerCase().includes(t); }).map((s) => {
                  const selected = fpSelectedIds.includes(s.id);
                  const atLimit = slabObj && fpSelectedIds.length >= slabObj.count && !selected;
                  const row = svcRows.find((r) => r.service_id === s.id);
                  return (
                    <div key={s.id} className={`rounded-md border px-3 py-2 ${selected ? "border-primary bg-primary/5" : "border-border"} ${atLimit ? "opacity-50" : ""}`} data-testid={`appt-fp-svc-${s.id}`}>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" checked={selected} disabled={atLimit} onChange={() => toggleFpSvc(s)} className="h-4 w-4 accent-[#C9A24B]" data-testid={`appt-fp-toggle-${s.id}`} />
                        <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{s.name}</p><p className="text-xs text-muted-foreground">{s.category} · {s.duration} min</p></div>
                        <span className="text-sm text-primary shrink-0">₹{s.actual_price ?? s.price}</span>
                      </div>
                      {selected && (
                        <div className="mt-2 pl-7">
                          <Select value={row?.staff_id || ""} onValueChange={(v) => setFpStaff(s.id, v)}>
                            <SelectTrigger className="bg-secondary h-9" data-testid={`appt-fp-staff-${s.id}`}><SelectValue placeholder="Select staff for this service" /></SelectTrigger>
                            <SelectContent>{fpStaffFor(s.id).map((st) => (
                              <SelectItem key={st.id} value={st.id} data-testid={`appt-fp-staff-opt-${s.id}-${st.id}`}><span className="flex items-center gap-2">{(st.photo || st.image) ? <img src={st.photo || st.image} alt="" className="h-5 w-5 rounded-full object-cover" /> : <span className="h-5 w-5 rounded-full bg-primary/20 text-primary text-[10px] grid place-items-center">{(st.name || "?")[0]}</span>}{st.name}</span></SelectItem>
                            ))}</SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  );
                })}
                {!(fpCfg.services || []).length && <p className="text-sm text-muted-foreground p-3 text-center">No eligible services configured. Add them in Service Management → Freedom Package.</p>}
              </div>
            </div>
          ) : (
          <div className="space-y-2">
            <Label className="text-xs">Services</Label>
            {svcRows.map((r, i) => (
              <div key={i} className="flex flex-wrap gap-2 items-center w-full" data-testid={`appt-svc-row-${i}`}>
                <div className="flex-1 min-w-0 basis-full sm:basis-0">
                  <ServiceSelect services={services} value={r.service_id}
                    onChange={(v) => setSvcRow(i, { service_id: v, duration: services.find((s) => s.id === v)?.duration || 30 })}
                    testid={`appt-service-${i}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <Select value={r.staff_id} onValueChange={(v) => setSvcRow(i, { staff_id: v })}>
                    <SelectTrigger className="bg-secondary w-full min-w-0 [&>span]:truncate" data-testid={`appt-staff-${i}`}><SelectValue placeholder="Staff" /></SelectTrigger>
                    <SelectContent>{branchStaff.map((s) => <SelectItem key={s.id} value={s.id}><span className="flex items-center gap-2">{(s.photo || s.image) ? <img src={s.photo || s.image} alt="" className="h-5 w-5 rounded-full object-cover" /> : <span className="h-5 w-5 rounded-full bg-primary/20 text-primary text-[10px] grid place-items-center">{(s.name || "?")[0]}</span>}{s.name}</span></SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Input type="number" value={r.duration} onChange={(e) => setSvcRow(i, { duration: Number(e.target.value) || 0 })} className="bg-secondary w-16 shrink-0" data-testid={`appt-duration-${i}`} />
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive shrink-0" disabled={svcRows.length === 1} onClick={() => delSvcRow(i)} data-testid={`appt-svc-del-${i}`}><Trash2 size={14} /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={addSvcRow} data-testid="appt-add-service"><Plus size={14} className="mr-1" /> Add Service</Button>
          </div>
          )}
          <div><Label className="text-xs">Date</Label><Input type="date" data-testid="appt-date" min={today()} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-secondary mt-1" /></div>
          <div><Label className="text-xs">Start Time</Label>
            {offMsg && <p className="text-sm text-destructive mt-1" data-testid="appt-staff-off">{offMsg}</p>}
            {!offMsg && !slots.length && form.date && <p className="text-xs text-muted-foreground mt-1" data-testid="appt-no-slots">No upcoming slots for this staff on the selected date.</p>}
            <div className="grid grid-cols-4 gap-2 mt-1 max-h-40 overflow-y-auto">
              {slots.map((s) => (
                <Button key={s.time} type="button" size="sm" disabled={!s.available}
                  data-testid={`slot-${s.time}`}
                  onClick={() => setForm({ ...form, start_time: s.time })}
                  className={`rounded-sm h-8 ${form.start_time === s.time ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"} ${!s.available ? "opacity-30" : ""}`}
                  variant="ghost">{to12h(s.time)}</Button>
              ))}
            </div>
          </div>
          {form.start_time && (
            <div className="bg-secondary rounded-sm p-3 space-y-1" data-testid="appt-schedule-preview">
              {schedule.map((r, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span>{to12h(r.start)}–{to12h(r.end)} · {services.find((s) => s.id === r.service_id)?.name || "—"}</span>
                  <span className="text-muted-foreground">{staff.find((s) => s.id === r.staff_id)?.name || "—"}</span>
                </div>
              ))}
              <div className="text-xs text-primary pt-1 border-t border-border/50">Total: {totalMin} min</div>
            </div>
          )}
        </div>
        {fpSlabA && (
          <div className="text-sm text-center py-1 border-t border-border/50" data-testid="appt-fp-summary">
            <span className="text-primary font-medium">Any {fpSlabA} – ₹{(slabObj?.price || 0).toLocaleString("en-IN")}</span>
            <span className="text-muted-foreground"> • {fpSelectedIds.length} service{fpSelectedIds.length === 1 ? "" : "s"} selected</span>
          </div>
        )}
        <DialogFooter><Button onClick={save} data-testid="appt-save" disabled={!form.start_time || !svcRows.some((r) => r.service_id && r.staff_id)} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Book</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
